import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type DeliverabilityResult, type ListSendingDomainsResult } from '@whinerp/client';
import { formatCount, formatPercent } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/** Deliverability (F-065): bounce and complaint rates against the lines that matter, and warm-up. */
export function DeliverabilityPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-065');
  const { can } = useSession();
  const allowed = enabled.enabled && can('campaign.send');
  const data = useQuery<DeliverabilityResult, ApiError>({ queryKey: ['deliverability'], queryFn: () => api.deliverability({}), enabled: allowed });
  const warmup = useMutation({
    mutationFn: (on: boolean) => api.setWarmup({ body: { on } }),
    onSuccess: (result) => queryClient.setQueryData(['deliverability'], result),
  });
  if (!allowed) return null;
  const pct = (value: number) => formatPercent(value, format, { decimals: 2 });
  return (
    <Panel data-region="deliverability">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('deliver.title')}</SectionLabel>
        {data.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : data.isError ? (
          <p className="text-dense text-danger-text">{errorText(data.error)}</p>
        ) : (
          <>
            <ul className="flex flex-wrap gap-4 text-dense">
              <li>{t('deliver.sent', { values: { count: formatCount(data.data.sent, format) } })}</li>
              <li data-bounce-rate className={data.data.bounceWarning ? 'text-danger-text' : 'text-ink'}>
                {t('deliver.bounces', { values: { rate: pct(data.data.bounceRate), line: pct(0.02) } })}
              </li>
              <li data-complaint-rate className={data.data.complaintWarning ? 'text-danger-text' : 'text-ink'}>
                {t('deliver.complaints', { values: { rate: pct(data.data.complaintRate), line: pct(0.001) } })}
              </li>
              <li>{t('deliver.suppressed', { values: { count: formatCount(data.data.suppressed, format) } })}</li>
            </ul>
            {data.data.bounceWarning || data.data.complaintWarning ? <p data-deliver-warning className="text-dense text-danger-text">{t('deliver.warning')}</p> : null}
            <div className="flex flex-wrap items-center gap-2">
              <span data-warmup className="text-meta text-ink-meta">
                {data.data.warmup.cap === null
                  ? data.data.warmup.day === null
                    ? t('deliver.noWarmup')
                    : t('deliver.warmedUp')
                  : t('deliver.warmupDay', {
                      values: {
                        day: String(data.data.warmup.day ?? 1),
                        sent: formatCount(data.data.warmup.sentToday, format),
                        cap: formatCount(data.data.warmup.cap, format),
                        held: formatCount(data.data.heldForWarmup, format),
                      },
                    })}
              </span>
              <Button intent="ghost" size="small" data-warmup-toggle disabled={warmup.isPending} onClick={() => warmup.mutate(data.data.warmup.day === null)}>
                {data.data.warmup.day === null ? t('deliver.startWarmup') : t('deliver.stopWarmup')}
              </Button>
            </div>
          </>
        )}
        <SendingDomains />
      </div>
    </Panel>
  );
}

/** Setup of sending domains: records to copy, and a verify that names the failing one. */
function SendingDomains(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  const domains = useQuery<ListSendingDomainsResult, ApiError>({ queryKey: ['sending-domains'], queryFn: () => api.listSendingDomains({}) });
  const add = useMutation({
    mutationFn: () => api.addSendingDomain({ body: { domain } }),
    onSuccess: (result) => {
      setDomain('');
      queryClient.setQueryData(['sending-domains'], result);
    },
  });
  const verify = useMutation({
    mutationFn: (domainId: string) => api.verifySendingDomain({ path: { domainId }, body: {} }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sending-domains'] }),
  });
  const refusal = add.error ?? verify.error ?? domains.error;
  return (
    <div className="flex flex-col gap-2 border-t border-t-rule pt-2">
      <SectionLabel>{t('deliver.domains')}</SectionLabel>
      {(domains.data?.domains ?? []).map((entry) => (
        <div key={entry.domainId} data-sending-domain={entry.domain} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-dense text-ink">{entry.domain}</span>
            <span data-domain-status={entry.status} className={entry.status === 'verified' ? 'text-meta text-success-text' : 'text-meta text-warning-text'}>
              {t(`deliver.domain.${entry.status}`)}
            </span>
            <Button intent="secondary" size="small" className="ms-auto" data-domain-verify disabled={verify.isPending} onClick={() => verify.mutate(entry.domainId)}>
              {t('deliver.verify')}
            </Button>
          </div>
          <ul className="flex flex-col gap-1">
            {entry.records.map((record) => (
              <li key={record.recordId} data-dns-record={record.kind} className="flex flex-col rounded-control bg-canvas p-2 text-meta">
                <span className="text-ink">
                  {record.recordType} · <code className="break-all">{record.host}</code>
                </span>
                <span className="flex items-center gap-2">
                  <code className="break-all text-ink-600">{record.expectedValue}</code>
                  <Button intent="ghost" size="small" data-dns-copy onClick={() => void navigator.clipboard?.writeText(record.expectedValue)}>
                    {t('deliver.copy')}
                  </Button>
                </span>
                {!record.verified ? <span data-dns-explanation className="text-warning-text">{record.explanation}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <input data-domain-input aria-label={t('deliver.domain')} placeholder="mail.example.com" className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink" value={domain} onChange={(e) => setDomain(e.target.value)} />
        <Button intent="secondary" size="small" data-domain-add disabled={add.isPending || domain.trim() === ''} onClick={() => add.mutate()}>
          {t('deliver.addDomain')}
        </Button>
      </div>
      {refusal ? <p className="text-dense text-danger-text">{errorText(refusal)}</p> : null}
    </div>
  );
}
